import { Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { SHARED_MODULES } from 'src/app/modules/shared-imports';
import { CalculatorPlatform, detectCalculatorPlatform } from './calculator-platform';

type Operator = '+' | '−' | '×' | '÷';

/** A genuine, general-purpose 4-function calculator — Windows 11 Calculator / iOS Calculator
 *  shaped, usable by tapping buttons OR typing on a physical keyboard. Deliberately a blank tool
 *  the user can do ANY calculation with (not tied to Qty/Price) — [startValue] only ever prefills
 *  a convenient starting number, never restricts what can be typed after.
 *
 *  Hosted inside a real <p-dialog> by the caller (never a popover/overlay anchored to a trigger
 *  element) — that anchor-positioning approach is exactly what broke visually the first time this
 *  was built. A dialog is centered on the viewport with no anchor math to get wrong, and it's the
 *  same container the main Quick Search dialog itself already uses without incident. */
@Component({
  selector: 'app-simple-calculator',
  standalone: true,
  imports: [SHARED_MODULES],
  templateUrl: './simple-calculator.component.html',
  styleUrls: ['./simple-calculator.component.css'],
})
export class SimpleCalculatorComponent implements OnChanges, OnInit {
  /** Optional starting number (e.g. a fetched cost/CBM or sale price) — purely a convenience
   *  prefill, never required, and never re-applied once the user has started typing (see
   *  ngOnChanges) — a background data refresh must never clobber an in-progress calculation. */
  @Input() startValue: number | null = null;
  @Input() startLabel: string | null = null;
  @Input() currencyCode: string | null = null;
  /** Gates the keyboard listener — only wired up while this calculator is actually the thing on
   *  screen, so it never steals keystrokes meant for the search box or anything else behind it. */
  @Input() active = false;

  @Output() result = new EventEmitter<number>();
  /** No-op unless the caller wires it up — lets this component stay usable standalone (no host
   *  dialog) while still giving every real caller a real close affordance. See the class doc
   *  comment: this widget has no header of its own (hosted in a `[showHeader]="false"` dialog),
   *  so the dialog otherwise has no visible way to close it — the old "close on second Escape"
   *  plan never actually worked (onKeydown below calls stopPropagation() on every Escape press,
   *  not just the first, so the host dialog's own Escape-to-close never got a chance to fire). */
  @Output() closeRequested = new EventEmitter<void>();

  display = '0';
  /** The small trail line above the display (e.g. "150 ×") — shown while composing an operation,
   *  same convention as the OS calculators this is modeled on. */
  trail = '';

  /** Which native calculator look to render (device-appropriate UI, per the redesign plan) —
   *  resolved once on init, not reactive to anything changing later (a device's platform never
   *  changes mid-session). See calculator-platform.ts for how this is decided. */
  platform: CalculatorPlatform = 'default';

  /** "Typed effect" — briefly true right after any key changes the display, driving a short CSS
   *  pulse so the eye actually registers the change rather than it silently swapping. Toggled via
   *  a real off-then-on cycle (see pulseDisplay) so rapid consecutive presses each re-trigger the
   *  animation instead of only the first one. */
  displayPulse = false;
  private pulseTimer: ReturnType<typeof setTimeout> | undefined;

  private previousValue: number | null = null;
  private pendingOperator: Operator | null = null;
  private awaitingNewOperand = false;
  private hasStarted = false;

  /** Which on-screen button should show its "pressed" visual state right now — set by a
   *  physical keyboard press so typing gives the same visual feedback as tapping/clicking,
   *  reusing each platform's existing real :active styling (see CSS — every `--kbd-press`
   *  selector is just appended onto the matching `:active` rule, not a separate look). Real
   *  pointer presses don't need this at all; :active already handles those natively.
   *
   *  Confirmed 2026-08-26 (a reported "only operators show a press effect" bug turned out to be
   *  a CSS-weight issue, not a logic one — onKeydown below already calls flashKey() identically
   *  for every key, digits included): operator buttons ALSO carry a separate, deliberately
   *  PERSISTENT `--op-active` class (driven by activeOperator, not this field) that stays on
   *  until the pending operation resolves — real Windows/iOS calculators do the same thing. That
   *  obvious, long-lived highlight was easy to mistake for "the keypress flash," while the
   *  actual brief kbd-press flash for digit/fn/eq keys was real but too visually subtle
   *  (particularly the Windows skin's old `scale(0.97); box-shadow: none`) to register. Fixed by
   *  giving `--kbd-press` a real, visible color change per skin — see the CSS. */
  kbdPressedKey: string | null = null;
  private kbdPressTimer: ReturnType<typeof setTimeout> | undefined;

  private flashKey(id: string): void {
    this.kbdPressedKey = null;
    clearTimeout(this.kbdPressTimer);
    requestAnimationFrame(() => {
      this.kbdPressedKey = id;
      this.kbdPressTimer = setTimeout(() => {
        if (this.kbdPressedKey === id) this.kbdPressedKey = null;
      }, 110);
    });
  }

  ngOnInit(): void {
    this.platform = detectCalculatorPlatform();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['startValue'] && !this.hasStarted && this.startValue != null) {
      this.display = this.formatNumber(this.startValue);
      this.pulseDisplay();
    }
  }

  /** Longer numbers shrink to fit on one line rather than scrolling off — the same auto-shrink
   *  behaviour iOS's own Calculator uses, applied on every platform since it's a genuine usability
   *  win, not just an iOS-specific flourish. */
  get displayFontSizeRem(): number {
    const len = this.display.replace('-', '').length;
    if (len > 9) return 1.3;
    if (len > 6) return 1.65;
    return 2.1;
  }

  private pulseDisplay(): void {
    this.displayPulse = false;
    clearTimeout(this.pulseTimer);
    // requestAnimationFrame forces a real reflow between off and on, so the CSS animation
    // restarts on every press — going straight from true to true wouldn't replay it.
    requestAnimationFrame(() => {
      this.displayPulse = true;
      this.pulseTimer = setTimeout(() => (this.displayPulse = false), 180);
    });
  }

  get activeOperator(): Operator | null {
    return this.pendingOperator;
  }

  get clearLabel(): string {
    return this.hasStarted && this.display !== '0' && this.pendingOperator == null ? 'C' : 'AC';
  }

  // ── Keyboard input — same underlying methods as tapping, single source of truth ───────────
  @HostListener('window:keydown', ['$event'])
  onKeydown(ev: KeyboardEvent): void {
    if (!this.active) return;
    const key = ev.key;
    if (/^[0-9]$/.test(key)) { this.pressDigit(key); this.flashKey('digit-' + key); ev.preventDefault(); return; }
    if (key === '.') { this.pressDecimal(); this.flashKey('decimal'); ev.preventDefault(); return; }
    if (key === '+') { this.pressOperator('+'); this.flashKey('op-+'); ev.preventDefault(); return; }
    if (key === '-') { this.pressOperator('−'); this.flashKey('op-−'); ev.preventDefault(); return; }
    if (key === '*') { this.pressOperator('×'); this.flashKey('op-×'); ev.preventDefault(); return; }
    if (key === '/') { this.pressOperator('÷'); this.flashKey('op-÷'); ev.preventDefault(); return; }
    if (key === '%') { this.pressPercent(); this.flashKey('percent'); ev.preventDefault(); return; }
    if (key === 'Enter' || key === '=') { this.pressEquals(); this.flashKey('eq'); ev.preventDefault(); return; }
    if (key === 'Backspace') { this.pressBackspace(); this.flashKey('backspace'); ev.preventDefault(); return; }
    // Escape clears when there's anything to clear (matches Windows Calculator); once already at
    // "AC" state (nothing left to clear), Escape instead closes the dialog via the same explicit
    // close button below — a second Escape "falling through" to the host dialog's own handler
    // was the original plan but never actually worked, since stopPropagation() below fired on
    // every press, not just the first.
    if (key === 'Escape') {
      ev.preventDefault();
      ev.stopPropagation();
      if (this.clearLabel === 'AC' && this.display === '0' && !this.trail) {
        this.closeRequested.emit();
      } else {
        this.pressAllClear();
        this.flashKey('ac');
      }
      return;
    }
  }

  requestClose(): void {
    this.closeRequested.emit();
  }

  pressDigit(digit: string): void {
    this.hasStarted = true;
    if (this.awaitingNewOperand || this.display === '0') {
      this.display = digit;
      this.awaitingNewOperand = false;
    } else {
      if (this.display.replace('-', '').length >= 12) return; // sane display cap
      this.display += digit;
    }
    this.pulseDisplay();
  }

  pressDecimal(): void {
    this.hasStarted = true;
    if (this.awaitingNewOperand) {
      this.display = '0.';
      this.awaitingNewOperand = false;
      this.pulseDisplay();
      return;
    }
    if (!this.display.includes('.')) this.display += '.';
    this.pulseDisplay();
  }

  pressBackspace(): void {
    if (this.awaitingNewOperand) return;
    this.display = this.display.length > 1 ? this.display.slice(0, -1) : '0';
    this.pulseDisplay();
  }

  pressAllClear(): void {
    if (this.clearLabel === 'C') {
      // "C" — clear just the current entry, keep any pending operator/trail (Windows convention).
      this.display = '0';
      this.awaitingNewOperand = false;
      this.pulseDisplay();
      return;
    }
    this.display = '0';
    this.trail = '';
    this.previousValue = null;
    this.pendingOperator = null;
    this.awaitingNewOperand = false;
    this.hasStarted = false;
    this.pulseDisplay();
  }

  pressToggleSign(): void {
    if (this.display === '0') return;
    this.display = this.display.startsWith('-') ? this.display.slice(1) : '-' + this.display;
    this.pulseDisplay();
  }

  pressPercent(): void {
    const current = parseFloat(this.display) || 0;
    this.display = this.formatNumber(current / 100);
    this.pulseDisplay();
  }

  pressOperator(op: Operator): void {
    this.hasStarted = true;
    const current = parseFloat(this.display) || 0;
    if (this.pendingOperator && !this.awaitingNewOperand) {
      this.previousValue = this.compute(this.previousValue ?? 0, current, this.pendingOperator);
      this.display = this.formatNumber(this.previousValue);
    } else {
      this.previousValue = current;
    }
    this.pendingOperator = op;
    this.trail = `${this.formatNumber(this.previousValue)} ${op}`;
    this.awaitingNewOperand = true;
    this.pulseDisplay();
  }

  pressEquals(): void {
    if (this.pendingOperator == null || this.previousValue == null) return;
    const current = parseFloat(this.display) || 0;
    this.trail = `${this.formatNumber(this.previousValue)} ${this.pendingOperator} ${this.formatNumber(current)} =`;
    const total = this.compute(this.previousValue, current, this.pendingOperator);
    this.display = this.formatNumber(total);
    this.previousValue = null;
    this.pendingOperator = null;
    this.awaitingNewOperand = true;
    this.pulseDisplay();
    this.result.emit(total);
  }

  private compute(a: number, b: number, op: Operator): number {
    switch (op) {
      case '+': return a + b;
      case '−': return a - b;
      case '×': return a * b;
      case '÷': return b === 0 ? 0 : a / b;
    }
  }

  private formatNumber(n: number): string {
    if (!isFinite(n)) return '0';
    // Trim floating-point noise (0.1+0.2 style) without permanently rounding real precision away.
    const rounded = Math.round(n * 1e9) / 1e9;
    return String(rounded);
  }
}
